import { api } from './api';

const basePath = ({ organizationId, projectId, resourceId }) => `/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}/resources/${encodeURIComponent(resourceId)}/workload-discovery`;
export const discoveryCapabilityPath = (scope) => `${basePath(scope)}/capability`;
export const discoveryScansPath = (scope) => `${basePath(scope)}/scans`;
export const discoveryScanPath = (scope, scanId) => `${discoveryScansPath(scope)}/${encodeURIComponent(scanId)}`;
export const discoverySchedulePath = (scope) => `${basePath(scope)}/schedule`;

export const newDiscoveryIntentKey = () => globalThis.crypto?.randomUUID?.() || `discovery-${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const workloadDiscoveryApi = {
  capability: (scope) => api.get(discoveryCapabilityPath(scope)),
  scans: (scope, limit = 20) => api.get(discoveryScansPath(scope), { params: { limit } }),
  start: (scope, collectors, idempotencyKey) => api.post(discoveryScansPath(scope), { collectors }, { headers: { 'Idempotency-Key': idempotencyKey } }),
  get: (scope, scanId) => api.get(discoveryScanPath(scope, scanId)),
  cancel: (scope, scanId, revision) => api.post(`${discoveryScanPath(scope, scanId)}/cancel`, { expected_revision: revision }),
  review: (scope, scanId, body, idempotencyKey) => api.post(`${discoveryScanPath(scope, scanId)}/review`, body, { headers: { 'Idempotency-Key': idempotencyKey } }),
  schedule: (scope) => api.get(discoverySchedulePath(scope)),
  updateSchedule: (scope, body) => api.put(discoverySchedulePath(scope), body),
};

export const SCAN_ACTIVE_STATES = ['requested', 'running'];
export const SCAN_REVIEWABLE_STATES = ['awaiting_review', 'partially_completed'];
export const DISCOVERY_COLLECTORS = ['os', 'systemd', 'processes', 'docker', 'podman', 'listening_ports'];

export const discoveryReason = (reason) => ({
  discovery_feature_disabled: 'Workload discovery is disabled by the operator.',
  discovery_not_enabled_for_resource: 'This server has not opted in to workload discovery.',
  authorization_unavailable: 'A current terminal access decision is unavailable.',
  terminal_access_not_authorized: 'You do not have permission to run discovery on this server.',
  authorization_actor_mismatch: 'The terminal authorization belongs to another user.',
  authorization_scope_mismatch: 'The terminal authorization does not match this server.',
  ssh_trust_not_verified: 'Server SSH trust is not verified. Enroll and confirm the Cloud Watcher CA first.',
  authorization_expired: 'Your just-in-time terminal access has expired. Request access again.',
  scan_already_running: 'A discovery scan is already running for this server.',
  rate_limited: 'This server has reached its discovery scan limit. Wait before scanning again.',
  schedule_not_allowed: 'Scheduled discovery is not allowed for your current access.',
}[reason] || `Discovery is unavailable: ${String(reason || 'unknown reason').replace(/_/g, ' ')}.`);

export const normalizeCapability = (payload = {}) => {
  const raw = payload.capability || payload;
  const reasons = raw.reasons || (raw.reason_code ? [raw.reason_code] : []);
  return {
    canScan: raw.can_scan ?? raw.enabled === true,
    reasons,
    collectors: raw.collectors || DISCOVERY_COLLECTORS,
    commands: raw.commands || {},
    limits: raw.limits || {},
    schedule: raw.schedule || null,
    verifiedSsh: raw.verified_ssh ?? !reasons.includes('ssh_trust_not_verified'),
    activeScanId: raw.active_scan_id || null,
  };
};

export const normalizeScan = (payload = {}) => {
  const scan = payload.scan || payload;
  return {
    ...scan,
    id: scan.id || scan.scan_id,
    state: scan.state || 'unknown',
    revision: Number(scan.revision || 0),
    collectors: scan.collectors || [],
    collector_results: scan.collector_results || [],
    candidates: scan.candidates || [],
  };
};

export const scanProgress = (scan) => {
  if (!scan) return null;
  const complete = scan.collector_results?.length || 0;
  const total = scan.collectors?.length || 0;
  if (scan.state === 'requested') return { label: 'Scan queued', detail: 'Waiting for a bounded discovery worker.', active: true };
  if (scan.state === 'running') return { label: `Scanning · ${complete}/${total || '—'} checks`, detail: 'Only fixed read-only commands are running. Raw output is never retained.', active: true };
  if (scan.state === 'partially_completed') return { label: 'Scan partially completed', detail: 'Some checks failed. Review the usable sanitized suggestions and the per-check errors.', active: false };
  if (scan.state === 'awaiting_review') return { label: 'Ready for review', detail: 'Nothing has been added to inventory. Review every suggestion first.', active: false };
  if (scan.state === 'cancelled') return { label: 'Scan cancelled', detail: 'No suggestions were attached automatically.', active: false };
  if (scan.state === 'failed') return { label: 'Scan failed', detail: 'No raw output was retained and nothing was attached.', active: false };
  if (scan.state === 'accepted' || scan.state === 'rejected') return { label: 'Review complete', detail: 'The decision and evidence are retained in scan history.', active: false };
  return { label: 'Scan state unknown', detail: 'Refresh before taking action.', active: false };
};

export const candidateApplication = (candidate) => ({
  name: candidate.name || 'Unnamed workload',
  purpose: '', application_type: candidate.application_type || 'other', environment: 'unclassified',
  owner_mode: 'inherit', owners: [], links: [], runtime_identifiers: (candidate.runtime_identifiers || []).map((item) => ({ kind: item.kind, id: item.id })),
  ports: (candidate.ports || []).map((item) => ({ name: item.name || '', port: Number(item.port), protocol: item.protocol || 'tcp', exposure: item.exposure || 'unknown' })), endpoints: [], version: '',
  health: { source: 'unknown', status: 'unknown', observed_at: null, stale_after_seconds: 300 },
  dependencies: [], data_stores: [], deployment_method: 'unknown',
  criticality_mode: 'inherit', criticality_override: null, lifecycle: 'active',
});

export const candidateEvidence = (candidate) => (candidate.evidence || []).map((item) => ({
  collector: item.collector || 'unknown', observedAt: item.observed_at || null,
  summary: item.summary || 'Sanitized evidence summary unavailable.',
}));
